import { trackEventView } from "../../hooks/useAnalyticsTracking";
import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { supabase } from "../../lib/supabase";
import type { EventPopup } from "../../lib/supabase";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";

const EventDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const [event, setEvent] = useState<EventPopup | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      const { data } = await supabase.from("event_popups").select("*").eq("id", id).maybeSingle();
      setEvent(data as EventPopup | null);
      if (data) {
        trackEventView(data.id, "event", data.title_vi ?? "", window.location.pathname);
      }
      setLoading(false);
    };
    if (id) fetch();
  }, [id]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    const form = e.currentTarget;
    const fields: Record<string, string> = {};
    const elements = form.elements;
    for (let i = 0; i < elements.length; i++) {
      const el = elements[i] as HTMLInputElement | HTMLTextAreaElement;
      if (el.name && el.value) fields[el.name] = el.value;
    }
    try {
      await supabase.from("event_registrations").insert({
        event_id: event?.id ?? "",
        event_name: event?.title_vi ?? "",
        name: fields.name ?? "",
        email: fields.email ?? "",
        phone: fields.phone ?? "",
        message: fields.message ?? "",
        source: "detail_page",
      });
      setSubmitted(true);
    } catch { setSubmitted(true); }
    finally { setSubmitting(false); }
  };

  if (loading) return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Navbar />
      <div className="flex items-center justify-center py-40">
        <i className="ri-loader-4-line animate-spin text-3xl text-[#004A99]" />
      </div>
      <Footer />
    </div>
  );

  if (!event) return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Navbar />
      <div className="text-center py-40">
        <i className="ri-calendar-event-line text-5xl text-gray-200 block mb-4" />
        <p className="text-gray-500 font-semibold">Không tìm thấy sự kiện</p>
        <Link to="/hoat-dong-hiep-hoi-va-su-kien" className="mt-4 inline-flex items-center gap-2 text-[#004A99] font-semibold text-sm cursor-pointer">
          <i className="ri-arrow-left-line" /> Quay lại danh sách
        </Link>
      </div>
      <Footer />
    </div>
  );

  const bgColor = event.bg_color ?? "#004A99";
  const highlights = (event.highlights ?? []) as string[];

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Navbar />

      {/* Hero Banner */}
      <div className="relative h-64 md:h-80 overflow-hidden" style={{ backgroundColor: bgColor }}>
        {event.image_url && (
          <img src={event.image_url} alt={event.title_vi} className="w-full h-full object-cover object-top" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10 max-w-[1200px] mx-auto">
          <div className="flex items-center gap-2 mb-3 text-sm">
            <Link to="/" className="text-white/60 hover:text-white cursor-pointer">Trang chủ</Link>
            <i className="ri-arrow-right-s-line text-white/40" />
            <Link to="/hoat-dong-hiep-hoi-va-su-kien" className="text-white/60 hover:text-white cursor-pointer">Hoạt Động & Sự Kiện</Link>
            <i className="ri-arrow-right-s-line text-white/40" />
            <span className="text-white/80 line-clamp-1">{event.title_vi}</span>
          </div>
          {event.is_active && (
            <span className="inline-flex items-center gap-1 bg-green-500 text-white text-xs px-3 py-1 rounded-full font-bold mb-3">
              <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
              Đang diễn ra
            </span>
          )}
          <h1 className="text-2xl md:text-3xl font-black text-white leading-tight">{event.title_vi}</h1>
        </div>
      </div>

      <div className="max-w-[1200px] mx-auto px-6 py-10">
        <div className="flex flex-col lg:flex-row gap-8">

          {/* Main content */}
          <div className="flex-1 min-w-0">
            {/* Meta info */}
            <div className="flex flex-wrap gap-4 mb-6 p-4 bg-white rounded-xl border border-gray-100">
              {event.event_date && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <div className="w-8 h-8 flex items-center justify-center bg-[#004A99]/10 rounded-lg">
                    <i className="ri-calendar-line text-[#004A99]" />
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-400">Thời gian</p>
                    <p className="font-semibold text-xs">{event.event_date}</p>
                  </div>
                </div>
              )}
              {event.event_location_vi && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <div className="w-8 h-8 flex items-center justify-center bg-[#004A99]/10 rounded-lg">
                    <i className="ri-map-pin-line text-[#004A99]" />
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-400">Địa điểm</p>
                    <p className="font-semibold text-xs">{event.event_location_vi}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Rich text description */}
            {event.description_vi && (
              <div
                className="event-rich-content bg-white rounded-xl border border-gray-100 p-6 mb-6 text-sm text-gray-700 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: event.description_vi }}
              />
            )}

            {/* Highlights */}
            {highlights.length > 0 && (
              <div className="bg-[#004A99]/5 border border-[#004A99]/15 rounded-xl p-5 mb-6">
                <p className="text-sm font-bold text-[#004A99] mb-3">🎯 Điểm Nổi Bật</p>
                <ul className="space-y-2">
                  {highlights.map((h, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-[#212529]">
                      <i className="ri-checkbox-circle-fill text-[#00AEEF] mt-0.5 flex-shrink-0" />
                      <span>{h}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* External link */}
            {event.button_link && (
              <a
                href={event.button_link}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="inline-flex items-center gap-2 px-6 py-3 text-white font-bold rounded-xl text-sm cursor-pointer whitespace-nowrap transition-opacity hover:opacity-90"
                style={{ backgroundColor: bgColor }}
              >
                <i className="ri-external-link-line" />
                Tham khảo chi tiết tại đây
              </a>
            )}
          </div>

          {/* Sidebar: Registration form */}
          <div className="lg:w-80 flex-shrink-0">
            <div className="bg-white rounded-xl border border-gray-100 p-5 sticky top-24">
              <h3 className="font-bold text-gray-800 text-sm mb-4 flex items-center gap-2">
                <i className="ri-user-add-line text-[#004A99]" />
                Đăng Ký Tham Gia
              </h3>
              {submitted ? (
                <div className="text-center py-8">
                  <div className="w-14 h-14 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-3">
                    <i className="ri-checkbox-circle-fill text-3xl text-green-500" />
                  </div>
                  <p className="font-bold text-gray-800 text-sm mb-1">Đăng ký thành công!</p>
                  <p className="text-xs text-gray-500">Chúng tôi sẽ liên hệ sớm nhất.</p>
                </div>
              ) : (
                <form id="event-detail-form" data-readdy-form onSubmit={handleSubmit} className="space-y-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Họ và tên *</label>
                    <input type="text" name="name" required placeholder="Nguyễn Văn A" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#004A99] bg-[#F8F9FA]" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Email *</label>
                    <input type="email" name="email" required placeholder="email@company.com" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#004A99] bg-[#F8F9FA]" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Số điện thoại</label>
                    <input type="tel" name="phone" placeholder="0901 000 000" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#004A99] bg-[#F8F9FA]" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Nội dung thắc mắc</label>
                    <textarea name="message" rows={3} maxLength={500} placeholder="Nhập nội dung... (tối đa 500 ký tự)" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#004A99] resize-none bg-[#F8F9FA]" />
                  </div>
                  <button type="submit" disabled={submitting} className="w-full py-3 text-white font-bold rounded-xl text-sm cursor-pointer whitespace-nowrap disabled:opacity-50 flex items-center justify-center gap-2 transition-opacity hover:opacity-90" style={{ backgroundColor: bgColor }}>
                    {submitting ? <><i className="ri-loader-4-line animate-spin" />Đang gửi...</> : <><i className="ri-send-plane-line" />Gửi Đăng Ký</>}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Back to list */}
      <div className="max-w-[1200px] mx-auto px-6 pb-10">
        <Link
          to="/hoat-dong-hiep-hoi-va-su-kien"
          className="inline-flex items-center gap-2 text-[#004A99] font-semibold text-sm hover:underline cursor-pointer"
        >
          <i className="ri-arrow-left-line" />
          Quay lại danh sách
        </Link>
      </div>

      <Footer />
    </div>
  );
};

export default EventDetailPage;
